import React, {useEffect, useRef, useState, useMemo, useContext} from 'react';
import {useBreakpointUp, IconButton, useBreakpointBetween, useBreakpointDown} from 'bonprix-design-system';
import Device from '@core/scripts/components/device';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {useAlpiSwatchOuterGap} from '@article-overview/components/hooks/use-alpi-swatch-outer-gap';
import {updateInputMethod} from '@core/scripts/initializers/intercept-what-input';
import {ColorSwatch, Segment, segmentStyle, plusGlyphColor, displaySegments, swatchContainsWhite, WHITE_SWATCH_BORDER} from './color-swatch-util';

interface ColorSwatchGridProps {
    colors: ColorSwatch[];
    activeIndex: number;
    onColorSelect: (index: number) => void;
}

// Quadrant rectangles (percent) per multipack count. See Figma AÜS-SES node 3388-10.
// 1: full. 2: top/bottom halves. 3: TL, TR, bottom full-width band. 4: quadrants.
// 5+: TL, TR, BL and a "+" in the bottom-right quadrant.
type Rect = {top: string; left: string; width: string; height: string};
const quadrantRects = (count: number): Rect[] => {
    const tl = {top: '0', left: '0', width: '50%', height: '50%'};
    const tr = {top: '0', left: '50%', width: '50%', height: '50%'};
    const bl = {top: '50%', left: '0', width: '50%', height: '50%'};
    const br = {top: '50%', left: '50%', width: '50%', height: '50%'};
    if (count <= 1) {
        return [{top: '0', left: '0', width: '100%', height: '100%'}];
    }
    if (count === 2) {
        return [
            {top: '0', left: '0', width: '100%', height: '50%'},
            {top: '50%', left: '0', width: '100%', height: '50%'},
        ];
    }
    if (count === 3) {
        return [tl, tr, {top: '50%', left: '0', width: '100%', height: '50%'}];
    }
    return [tl, tr, bl, br];
};

const ColorSwatchGrid: React.FC<ColorSwatchGridProps> = ({colors, activeIndex, onColorSelect}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [index, setIndex] = useState(0);
    const [touchPositionX, setTouchPositionX] = useState(0);
    const currentSlidePosition = useRef(0);

    const breakpointIsLDown = useBreakpointDown('xl'); // 0-991px
    const isBreakPointXlOrXXL = useBreakpointBetween('xl', 'xxxl'); // 992-1599px
    const isBreakPointXXXL = useBreakpointUp('xxxl'); // 1600px+
    const {texts} = useContext(articleOverviewStaticContext);
    const gap = 8; // 8px gap between swatches
    const outerGap = useAlpiSwatchOuterGap();
    const isTouch = Device.isTouch();

    // Fix Issue: PDS-344
    // Backend can't handle iPad agent and isTouch coming FALSE, the fix is re-checking by "what-input" when component is mounted
    useEffect(() => {
        if ('whatInput' in window) {
            const whatInput = window.whatInput as any;
            const current = whatInput.ask('input');
            updateInputMethod(current);
        }
    }, []);

    const visibleSwatches = useMemo(() => {
        if (breakpointIsLDown) {
            // S-L: 360-991px
            return 4.8;
        }
        if (isBreakPointXlOrXXL) {
            // XL-XXL: 992-1599px
            if (isTouch) {
                return 5.9;
            }
            return 6;
        }
        // 3XL: 1600-1900px (and above)
        if (isTouch) {
            return 7.5;
        }
        return 8;
    }, [isTouch, isBreakPointXlOrXXL, isBreakPointXXXL, breakpointIsLDown]);

    useEffect(() => {
        const calculateWidth = () => {
            // Find the .alpi__image element in the parent hierarchy
            const alpiImage = containerRef.current?.closest('.alpi')?.querySelector('.alpi__image');
            if (alpiImage) {
                const width = alpiImage.getBoundingClientRect().width;
                // Subtract outerGap for padding
                setContainerWidth(width - outerGap);
            }
        };

        calculateWidth();
        window.addEventListener('resize', calculateWidth);
        return () => window.removeEventListener('resize', calculateWidth);
    }, [outerGap]);

    // Calculate swatch size to show X.5 elements with gaps
    // For N visible swatches, we have (N-1) gaps between them when N is integer
    const totalGaps = (Math.ceil(visibleSwatches) - 1) * gap;
    const swatchSize = containerWidth > 0 ? Math.floor((containerWidth - totalGaps) / visibleSwatches) : 0;

    const displayColors = colors.slice(0, Math.min(colors.length, 12));
    const hasMoreColors = colors.length > displayColors.length;

    // Check if scrolling is needed based on actual visible swatches
    const needsScroll = displayColors.length > Math.floor(visibleSwatches);

    // Calculate current X position for transform
    const currentXPosition = useMemo(() => {
        if (!sliderRef.current || index === 0) {
            return 0;
        }
        const slideWidth = swatchSize + gap;
        // When scrolling, we want to hide the first swatch AND its following gap
        // So the second swatch starts at the beginning of the container
        return index * slideWidth * -1;
    }, [index, swatchSize, gap]);

    // Check if arrow buttons should be shown
    const isLeftButton = !isTouch && index > 0 && needsScroll;
    const isRightButton = useMemo(() => {
        if (isTouch || !needsScroll) return false;
        const totalSwatchesWidth = displayColors.length * swatchSize + (displayColors.length - 1) * gap;
        const maxScrollableDistance = totalSwatchesWidth - containerWidth;
        const currentScrollDistance = index * (swatchSize + gap);
        return currentScrollDistance < maxScrollableDistance;
    }, [isTouch, needsScroll, index, displayColors.length, swatchSize, gap, containerWidth]);

    // Arrow button click handlers
    const onSliderButtonClick = (isSlideRight: boolean) => {
        const slideStep = breakpointIsLDown ? 1 : 2; // Slide by 2 on xl and up, 1 on smaller screens
        if (isSlideRight) {
            slideRight(index, slideStep);
        } else {
            slideLeft(index, slideStep);
        }
    };

    const slideRight = (currentIndex: number, step: number) => {
        const totalSwatchesWidth = displayColors.length * swatchSize + (displayColors.length - 1) * gap;
        const maxScrollableDistance = Math.max(0, totalSwatchesWidth - containerWidth);
        const maxIndex = maxScrollableDistance / (swatchSize + gap);
        const newIndex = Math.min(maxIndex, currentIndex + step);
        lockInSliderPosition(newIndex);
    };

    const slideLeft = (currentIndex: number, step: number) => {
        const newIndex = Math.max(0, currentIndex - step);
        lockInSliderPosition(newIndex);
    };

    // Touch handlers (similar to AlpiIpicSlider) - only for mobile
    const handleTouchStart = (e: React.TouchEvent) => {
        if (!needsScroll) return;
        const firstTouch = e.touches[0];
        if (!firstTouch) return;
        setTouchPositionX(firstTouch.clientX);
        currentSlidePosition.current = currentXPosition;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!needsScroll || !sliderRef.current) return;
        const firstTouch = e.touches[0];
        if (!firstTouch) return;
        const currentTouch = firstTouch.clientX;
        const diff = touchPositionX - currentTouch;
        // Direct transform without transition for immediate response
        sliderRef.current.style.transition = 'none';
        sliderRef.current.style.transform = `translateX(${-diff + currentXPosition}px)`;
        currentSlidePosition.current = -diff + currentXPosition;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!needsScroll || !sliderRef.current) return;
        const slideWidth = swatchSize + gap;
        const closestIndex = -Math.round(currentSlidePosition.current / slideWidth);
        const changedTouch = e.changedTouches[0];

        if (changedTouch && touchPositionX > changedTouch.clientX) {
            // Slide right - calculate max index the same way as slideRight
            const totalSwatchesWidth = displayColors.length * swatchSize + (displayColors.length - 1) * gap;
            const maxScrollableDistance = Math.max(0, totalSwatchesWidth - containerWidth);
            const maxIndex = maxScrollableDistance / (swatchSize + gap);
            const newIndex = Math.min(maxIndex, Math.max(0, closestIndex));
            lockInSliderPosition(newIndex);
        } else if (changedTouch && touchPositionX < changedTouch.clientX) {
            // Slide left
            const newIndex = Math.max(0, closestIndex);
            lockInSliderPosition(newIndex);
        }
    };

    const lockInSliderPosition = (newIndex: number) => {
        if (!sliderRef.current) return;
        // Re-enable transition for smooth animation to final position
        sliderRef.current.style.transition = 'transform 150ms linear';
        if (newIndex === index) {
            sliderRef.current.style.transform = `translateX(${currentXPosition}px)`;
        } else {
            setIndex(newIndex);
        }
    };

    const touchAttributes = needsScroll
        ? {
              onTouchStart: handleTouchStart,
              onTouchEnd: handleTouchEnd,
              onTouchMove: handleTouchMove,
          }
        : {};

    const containerStyles = useMemo(
        () => ({
            paddingLeft: `${outerGap / 2}px`,
            paddingRight: `${outerGap / 2}px`,
        }),
        [outerGap],
    );

    const sliderStyles = useMemo(
        () => ({
            gap: `${gap}px`,
            transform: `translateX(${currentXPosition}px)`,
        }),
        [gap, currentXPosition],
    );

    // When the whole swatch already carries the grey contour (it contains white), inner segment
    // borders (e.g. the farblos outline) are dropped so only the single outer frame shows.
    const renderQuadrant = (segment: Segment, rect: Rect, key: number, suppressBorder: boolean) => {
        const style = segmentStyle(segment);
        return (
            <span
                key={key}
                className="absolute"
                style={{
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    background: style.background,
                    backgroundSize: style.backgroundSize,
                    border: suppressBorder ? undefined : style.border,
                    boxSizing: 'border-box',
                }}
            />
        );
    };

    const renderSegments = (segments: Segment[], suppressBorder: boolean) => {
        const count = segments.length;
        const rects = quadrantRects(count);
        if (count <= 4) {
            return segments.map((segment, i) => renderQuadrant(segment, rects[i]!, i, suppressBorder));
        }
        // 5+: first three colors fill TL/TR/BL, the "+" sits over the fourth color in BR.
        const visible = segments.slice(0, 3).map((segment, i) => renderQuadrant(segment, rects[i]!, i, suppressBorder));
        const fourth = segments[3]!;
        const br = {top: '50%', left: '50%', width: '50%', height: '50%'};
        return [
            ...visible,
            renderQuadrant(fourth, br, 3, suppressBorder),
            <span
                key="plus"
                className="absolute flex items-center justify-center"
                style={{
                    // Center on the bottom-right quadrant's centre point (75%, 75%) and let the
                    // 24px glyph overflow symmetrically rather than being boxed into the 50% quadrant.
                    top: '75%',
                    left: '75%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '24px',
                    lineHeight: '24px',
                    color: plusGlyphColor(fourth),
                }}
            >
                +
            </span>,
        ];
    };

    const renderSwatch = (swatch: ColorSwatch, idx: number) => {
        const isLastSwatch = hasMoreColors && idx === displayColors.length - 1;
        const isActive = idx === activeIndex;
        const segments = displaySegments(swatch);
        const containsWhite = swatchContainsWhite(segments);

        return (
            <div key={idx} className="flex flex-col flex-shrink-0" style={{width: `${swatchSize}px`}}>
                <button
                    className="relative cursor-pointer p-0"
                    onClick={() => onColorSelect(idx)}
                    onMouseOver={() => !isTouch && onColorSelect(idx)}
                    aria-label={swatch.name || `Color ${idx + 1}`}
                    style={{
                        width: `${swatchSize}px`,
                        height: `${swatchSize}px`,
                        borderRadius: '2px',
                        overflow: 'hidden',
                        border: containsWhite ? WHITE_SWATCH_BORDER : 0,
                        background: 'transparent',
                    }}
                >
                    {renderSegments(segments, containsWhite)}
                    {isLastSwatch && (
                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-white/80 rounded-full flex items-center justify-center text-[10px] font-bold text-gray-800">
                            +{colors.length - displayColors.length}
                        </span>
                    )}
                </button>
                <div
                    className="mt-1"
                    style={{
                        height: '3px',
                        backgroundColor: isActive ? '#000000' : 'transparent',
                    }}
                />
            </div>
        );
    };

    return (
        <div ref={containerRef} className="flex flex-col my-2 relative" style={containerStyles}>
            <div
                className="overflow-hidden"
                style={{width: containerWidth > 0 ? `${containerWidth}px` : '100%'}}
                {...touchAttributes}
            >
                <div
                    ref={sliderRef}
                    className="flex transition-transform duration-150 ease-linear"
                    style={sliderStyles}
                >
                    {displayColors.map(renderSwatch)}
                </div>
            </div>
            {isLeftButton && (
                <div className="ipic__slider-button color-swatch prev flex justify-center -mt-[3px]">
                    <IconButton
                        icon="arrow-left"
                        variant="tertiary"
                        label={texts?.prevPage || 'Previous'}
                        onClick={() => onSliderButtonClick(false)}
                    />
                </div>
            )}
            {isRightButton && (
                <div className="ipic__slider-button color-swatch next flex justify-center -mt-[3px]">
                    <IconButton
                        icon="arrow-right"
                        variant="tertiary"
                        label={texts?.nextPage || 'Next'}
                        onClick={() => onSliderButtonClick(true)}
                    />
                </div>
            )}
        </div>
    );
};

export default ColorSwatchGrid;
